import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import { WagerItem, handlePaginateGames } from "store/liveOps/wagers/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import Button from "components/buttons/Button";
import SearchInput from "components/inputs/SearchInput/SearchInput";

import { Modal } from "components/Modal/Modal";
import styles from "./GameActionModal.module.scss";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

const AddToWager: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isFetched, setFetched] = useState<boolean>(false);
  const [wagersList, setWagersList] = useState<WagerItem[]>([]);
  const [wagersListFull, setWagersListFull] = useState<WagerItem[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedWager, setSelectedWager] = useState<number>();
  const selectedItems = useAppSelector(
    (store) => store.wagers.selectedItemsGames,
  );

  const handleLoadWagers = async () => {
    setFetched(false);
    try {
      const result = await instance.get(
        `${apiRoutes.wagersList}?limit=1000&offset=0`,
      );
      setWagersListFull(result.data.items);
    } catch {
      console.log("handleLoadWagers error");
    }
    setFetched(true);
  };

  useEffect(() => {
    handleLoadWagers();
  }, []);

  useEffect(() => {
    setWagersList(
      wagersListFull.filter((el) =>
        el.title?.toLowerCase().includes(searchValue?.toLowerCase()),
      ),
    );
  }, [wagersListFull, searchValue]);

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
  };

  const handleApply = async () => {
    try {
      await instance.put(
        `${apiRoutes.wagerAddGames}/${selectedWager ?? 0}/add-games`,
        {
          gameIds: selectedItems,
        },
      );
      dispatch(
        showNotification({
          title: t("success"),
          message: t("addGamesSuccess"),
          type: NotificationTypes.success,
        }),
      );
      handleCloseClick();
      dispatch(handlePaginateGames(1));
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("addGamesError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const handleSegmentClick = (id: number) => {
    if (selectedWager === id) {
      setSelectedWager(undefined);
    } else {
      setSelectedWager(id);
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header className={styles.addToWagerTitle}>
        {t("gamesAddToWager")}
      </Modal.Header>
      <Modal.Content className={styles.addToLiveOpsEntityContentWrapper}>
        {isFetched ? (
          <div>
            <div className="games-order-search">
              <SearchInput
                placeholder={t("searchWagerList")}
                value={searchValue}
                onChange={onSearch}
                isSmall
              />
            </div>
            <div className="games-order-list segments-order-list wagers-order-list">
              {isFetched ? (
                <>
                  <div className="item">
                    <div className="id">ID</div>
                    <div className="name">{t("name").toUpperCase()}</div>
                    <div className="type">
                      {t("contribution").toUpperCase()}
                    </div>
                    <div className="description">
                      {t("description").toUpperCase()}
                    </div>
                    <div className="active">{t("active").toUpperCase()}</div>
                  </div>
                  {wagersList.map((el) => (
                    <div
                      className={`item ${selectedWager === el.id ? "active" : ""}`}
                      key={el.id}
                      onClick={() => {
                        handleSegmentClick(el.id ?? 0);
                      }}
                    >
                      <div className="id">{el.id}</div>
                      <div className="name">{el.title}</div>
                      <div className="type">{el.contribution}</div>
                      <div className="description">{el.description}</div>
                      <div className="active">
                        <div
                          className={`active-label ${el.isActive ? "active" : ""}`}
                        >
                          {el.isActive ? "On" : "Off"}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <ModalTabSkeleton />
              )}
            </div>
          </div>
        ) : (
          <ModalTabSkeleton />
        )}
      </Modal.Content>
      <Modal.Footer>
        <Button isSecondary txt={t("cancel")} handleClick={handleCloseClick} />
        <Button isPrimary handleClick={handleApply} txt={t("save")} />
      </Modal.Footer>
    </Modal>
  );
};

export default AddToWager;
